import {clsx} from 'clsx'
import {AlertIcon, CheckIcon, XIcon} from '@primer/octicons-react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {AnchoredOverlay, IconButton} from '@primer/react'
import {useState} from 'react'

import styles from './ListItem.module.css'

type ListItemProps = {
  state: string
  avatarLabel?: string
  avatarUrl?: string
  hovercardUrl?: string
  title: string
  description?: string
  warning?: string
  titleClass?: string
  paddingY?: number
  paddingLeft?: number
}

function WarningOverlay({title, warning}: {title: string; warning: string}) {
  const [open, setOpen] = useState(false)

  return (
    <AnchoredOverlay
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      side="outside-top"
      align="center"
      width="small"
      overlayProps={{role: 'dialog', 'aria-label': `Warning for ${title}`}}
      renderAnchor={props => (
        <IconButton
          {...props}
          icon={AlertIcon}
          size="small"
          variant="invisible"
          aria-label={`View warning for ${title}`}
          description="View warning"
        />
      )}
    >
      <p className="p-3 m-0">{warning}</p>
    </AnchoredOverlay>
  )
}

export function ListItem({
  state,
  avatarLabel,
  avatarUrl,
  hovercardUrl,
  title,
  description,
  warning,
  titleClass,
  paddingY,
  paddingLeft,
}: ListItemProps) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const Icon = state === 'success' ? CheckIcon : XIcon
  return (
    <li
      style={{
        paddingBlock: `${(paddingY ?? 2) * 4}px`,
        paddingLeft: `${(paddingLeft ?? 2) * 4}px`,
      }}
      className={styles.ListItemContainer}
    >
      <Icon size={16} className={state === 'success' ? styles.iconSuccess : styles.iconFailure} />
      {avatarUrl && (
        <GitHubAvatar
          aria-label={avatarLabel}
          src={avatarUrl}
          {...(hovercardUrl ? getHovercardAttributesForType('user', {url: hovercardUrl}) : {})}
        />
      )}
      <span className={clsx(titleClass, !titleClass && styles.titleBold)}>{title}</span>
      {description ? <span className={styles.ListItemText}>{description}</span> : null}
      {warning && <WarningOverlay title={title} warning={warning} />}
    </li>
  )
}
